/**
 * ServerError — 500 error page.
 *
 * Per J-X8: "Something went wrong on our end"; "Retry" + "Return home" CTAs;
 * pre-filled "Contact support" with error reference ID; reference ID (UUID) shown.
 *
 * Cross-cutting invariants:
 *   - Inv 12: no console.log
 *   - Inv 13: no `any`
 *   - Inv 14: internal CTAs via <Button onClick>; mailto via <Link href="mailto:...">
 *   - Inv 15: atoms from @/components/ui barrel only (Button, Eyebrow, Heading, Link)
 */
import type { ReactElement } from 'react';
import { Button, Eyebrow, Heading, Link } from '@/components/ui';
import { ErrorPageChrome } from './ErrorPageChrome.tsx';

export interface ServerErrorPageProps {
  /** UUID reference ID for support correlation. Auto-generates if not provided. */
  referenceId?: string;
  /** Optional callback for retry action. Defaults to window.location.reload(). */
  onRetry?: () => void;
}

export function ServerErrorPage({ referenceId, onRetry }: ServerErrorPageProps = {}): ReactElement {
  const ref = referenceId ?? crypto.randomUUID();
  const supportMailto = `mailto:support@bimassess.com?subject=Error%20reference%20${ref}&body=Hi%20team%2C%0A%0AI%20encountered%20an%20error%20with%20reference%20ID%3A%20${ref}%0A%0APlease%20help.`;

  function handleRetry(): void {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  }

  return (
    <ErrorPageChrome>
      <Eyebrow>500 Server Error</Eyebrow>
      <Heading level="h1" className="mt-6">
        Something went wrong on our end.
      </Heading>
      <p className="mt-4 font-sans text-body-lg text-muted">
        We&apos;re having trouble processing your request. Our team has been notified.
      </p>
      <div className="mt-6 rounded-md border border-line bg-bg-warm px-4 py-3">
        <p className="font-mono text-mono text-muted">
          Reference ID:{' '}
          <span className="font-medium text-ink" data-testid="error-reference-id">
            {ref}
          </span>
        </p>
        <p className="mt-1 font-sans text-small text-muted">
          Please include this when contacting support.
        </p>
      </div>
      <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
        <Button variant="primary" onClick={handleRetry}>
          Retry
        </Button>
        <Button
          variant="secondary"
          onClick={() => { window.location.href = '/'; }}
        >
          Return home
        </Button>
        <Link href={supportMailto}>
          Contact support
        </Link>
      </div>
    </ErrorPageChrome>
  );
}
